/**
 *
 * EditRoute
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withLeaflet, Polyline } from 'react-leaflet';
import { TRAVELMODE_DONE } from 'utils/directions/constants';
import withSettings from 'containers/SettingsProvider/withSettings';
import { editColor } from 'styles/colors';
import HoverMarker from './HoverMarker';
import EditMarker from './EditMarker';
import { LEG_FIRST, LEG_MIDDLE, LEG_LAST } from './constants';
import fitOnMap from '../../utils/fitOnMap';
import { VIEW_ZOOM } from '../Map/constants';
import PathMenu from './PathMenu';
import getKmMarkers from '../RoutePath/getKmMarkers';

/* eslint-disable jsx-a11y/mouse-events-have-key-events */
class EditRoute extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { hover: null, clicked: null, dragging: null };
    this.handleHover = this.handleHover.bind(this);
    this.resetHover = this.resetHover.bind(this);
    this.editing = false;
  }

  static isEditing(route) {
    return Array.isArray(route.legs) && route.legs.length > 0;
  }

  componentDidUpdate() {
    const { workingRoute, leaflet } = this.props;
    if (
      !this.editing &&
      EditRoute.isEditing(workingRoute) &&
      workingRoute.bounds
    ) {
      this.editing = true;
      fitOnMap(leaflet.map, workingRoute.bounds, { zoom: VIEW_ZOOM + 2 });
    }
  }

  handleHover = leg => event => {
    this.setState(
      prev => (prev.hover || prev.dragging ? {} : { hover: { leg, event } }),
    );
  };

  handleClosePopup = () => {
    this.setState({ clicked: null });
  };

  handleClick = leg => ev => {
    const clicked = { leg, position: ev.latlng };
    this.setState({ clicked });
  };

  handleDrag = dragging => {
    if (!dragging || !dragging.isMarker) {
      this.setState({ dragging });
      return;
    }
    this.setState(prev => {
      if (prev.dragging && prev.dragging.nextLegId !== undefined) {
        return {
          dragging: { ...dragging, nextLegId: prev.dragging.nextLegId },
        };
      }
      const { workingRoute } = this.props;
      const nextIndex = workingRoute.legs.indexOf(dragging.legId) + 1;
      const nextLegId = workingRoute.legs[nextIndex] || null;
      return { dragging: { ...dragging, nextLegId } };
    });
  };

  resetHover = () => {
    this.setState({ hover: null });
  };

  getType(idx) {
    const { workingRoute } = this.props;
    if (idx === workingRoute.legs.length - 1) {
      return LEG_LAST;
    }
    return idx === 0 ? LEG_FIRST : LEG_MIDDLE;
  }

  isLegTemporary = leg => {
    const { dragging } = this.state;
    if (dragging && [dragging.legId, dragging.nextLegId].includes(leg.id)) {
      return true;
    }
    return leg.routing !== TRAVELMODE_DONE;
  };

  getDraggingPoints() {
    const { workingRoute } = this.props;
    const { dragging } = this.state;
    const leg = workingRoute[dragging.legId];
    if (!leg) {
      return [];
    }
    const result = [];
    if (leg.points.length > 1) {
      result.push(leg.points[0]);
    }
    result.push(dragging.latlng);
    if (!dragging.isMarker) {
      result.push(leg.lastPoint);
    } else if (dragging.nextLegId) {
      result.push(workingRoute[dragging.nextLegId].lastPoint);
    }
    return result;
  }

  render() {
    const { workingRoute, travelMode, leaflet, units } = this.props;
    const { hover, clicked, dragging } = this.state;
    if (!EditRoute.isEditing(workingRoute)) {
      this.editing = false;
      return null;
    }
    const legs = workingRoute.legs.map(id => workingRoute[id]);
    const points = workingRoute.legs.flatMap(id => workingRoute[id].points);
    const kmMarkers = getKmMarkers(points, leaflet.map.getZoom(), units);

    return (
      <React.Fragment>
        {legs
          .slice(1)
          .map(leg => (
            <Polyline
              key={`p_${leg.id}`}
              positions={leg.points}
              color={editColor}
              opacity={this.isLegTemporary(leg) ? 0.25 : 0.5}
              weight={4}
              onMouseover={this.handleHover(leg)}
              onMouseout={this.resetHover}
              onClick={this.handleClick(leg)}
              interactive
            />
          ))}
        {dragging && (
          <Polyline
            positions={this.getDraggingPoints()}
            color={editColor}
            opacity={0.15}
            weight={4}
          />
        )}
        {kmMarkers}
        {legs.map((leg, idx) => (
          <EditMarker
            key={`c_${leg.id}`}
            leg={leg}
            type={this.getType(idx)}
            onDrag={this.handleDrag}
          />
        ))}
        {clicked && <PathMenu onClose={this.handleClosePopup} {...clicked} />}
        <HoverMarker
          {...hover}
          travelMode={travelMode}
          onDrag={this.handleDrag}
          onClick={this.handleClick}
        />
      </React.Fragment>
    );
  }
}

EditRoute.propTypes = {
  workingRoute: PropTypes.object,
  leaflet: PropTypes.object,
  travelMode: PropTypes.string,
  units: PropTypes.string,
  movePoint: PropTypes.func,
};

export default compose(
  withLeaflet,
  withSettings(['units']),
)(EditRoute);
